import React, { useEffect, useState } from "react";
import { get, post, put } from "../../../redux/ApiHelper";
import { errorToast, successToast } from "../../../utils/toast.util";
import { ExaminationCreationRequestInterface } from "../../../interfaces/examination-creation-request.interface";
import { QuestionPaperInterface } from "../../../interfaces/question-paper.interface";
import { QuestionInterface } from "../../../interfaces/question.interface";
import {
    Box,
    Typography,
    Chip,
    Button,
    TextField,
} from "@mui/material";
import { Visibility } from "@mui/icons-material";
import TableComponent from "../../../components/table/TableComponent";
import { Column } from "../../../interfaces/table.interface";
import { RootState } from "../../../redux/store";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { QuestionPaperFormDialog } from "../../../components/examination/QuestionPaperDialogs";
import { joinSubjectChips, normalizeSubjectList } from "../../../utils/subjectChips.util";

const resolveId = (value: any): string | undefined => {
    if (!value) return undefined;
    if (typeof value === "object") {
        const id = value._id ?? value.id;
        return id ? String(id) : undefined;
    }
    return String(value);
};

const Requests = () => {
    const navigate = useNavigate();
    const [requests, setRequests] = useState<ExaminationCreationRequestInterface[]>([]);
    const [filteredRequests, setFilteredRequests] = useState<ExaminationCreationRequestInterface[]>([]);
    const [searchQuery, setSearchQuery] = useState<string>("");
    const [formModalOpen, setFormModalOpen] = useState<boolean>(false);
    const [selectedRequest, setSelectedRequest] = useState<ExaminationCreationRequestInterface | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [subjects, setSubjects] = useState<string[]>([]);
    const [availableQuestions, setAvailableQuestions] = useState<QuestionInterface[]>([]);
    const { signedAdmin } = useSelector((state: RootState) => state.admin);
    const [requestMeta, setRequestMeta] = useState<{
        examinationCreationRequestId: string | null;
        tuitionPeriodId: any | null;
    }>({ examinationCreationRequestId: null, tuitionPeriodId: null });

    // Form state
    const [formData, setFormData] = useState<Partial<QuestionPaperInterface>>({
        title: "",
        description: "",
        subject: "",
        class: "",
        examType: "mandatory",
        examStartDateTime: "",
        durationInMinutes: 0,
        fullMarks: 0,
        passMarks: 0,
        questions: [],
    });

    // Validation errors
    const [errors, setErrors] = useState<{
        title?: string;
        subject?: string;
        class?: string;
        examStartDateTime?: string;
        durationInMinutes?: string;
        fullMarks?: string;
        passMarks?: string;
        questions?: string;
    }>({});

    useEffect(() => {
        fetchRequests();
        fetchSubjects();
    }, []);

    useEffect(() => {
        if (formData.subject) {
            const seeds = (formData.questions || []).filter(
                (q: any) => q && typeof q === "object" && q._id
            );
            fetchQuestionsBySubject(formData.subject, seeds);
        } else {
            setAvailableQuestions([]);
        }
    }, [formData.subject]);

    useEffect(() => {
        const lowerQuery = searchQuery.toLowerCase();
        const filtered = requests.filter((req) => {
            const description = req.description?.toLowerCase() || "";
            const tuitionPeriod = req.tuitionPeriod;
            const studentName =
                typeof tuitionPeriod === "object" && tuitionPeriod?.student
                    ? (tuitionPeriod.student as any)?.name?.toLowerCase() || ""
                    : "";
            return description.includes(lowerQuery) || studentName.includes(lowerQuery);
        });
        setFilteredRequests(filtered);
    }, [requests, searchQuery]);

    const fetchRequests = async () => {
        try {
            const response = await get("/admin/examination/creation-requests");
            setRequests(response.data || []);
            setFilteredRequests(response.data || []);
        } catch (error) {
            console.error("Error fetching requests:", error);
            errorToast("Failed to fetch examination creation requests");
        }
    };

    const fetchSubjects = async () => {
        try {
            const response = await get("/admin/subject/get-all");
            setSubjects(response.subjects || []);
        } catch (error) {
            console.error("Error fetching subjects:", error);
        }
    };

    const fetchQuestionsBySubject = async (subject: string, seedQuestions: any[] = []) => {
        try {
            const response = await get("/admin/examination/questions", { subject });
            const bank = response.data || [];
            const byId = new Map<string, QuestionInterface>();
            for (const q of [...seedQuestions, ...bank]) {
                if (!q || typeof q !== "object" || !(q as any)._id) continue;
                byId.set(String((q as any)._id), q as QuestionInterface);
            }
            setAvailableQuestions(Array.from(byId.values()));
        } catch (error) {
            console.error("Error fetching questions:", error);
            const seeds = seedQuestions.filter((q) => q && typeof q === "object" && q._id);
            setAvailableQuestions(seeds);
        }
    };

    const handleOpenModal = async (request: ExaminationCreationRequestInterface) => {
        setSelectedRequest(request);

        const tuitionPeriodId = resolveId(request.tuitionPeriod);
        setRequestMeta({ examinationCreationRequestId: request._id, tuitionPeriodId });

        const period =
            typeof request.tuitionPeriod === "object" ? (request.tuitionPeriod as any) : null;
        const studentClass = String(period?.student?.class || "");
        const periodSubjects = joinSubjectChips(normalizeSubjectList(period?.subjects));
        const existingPaperId = resolveId(request.questionPaper);
        if (existingPaperId) {
            // If question paper exists, fetch it for editing
            try {
                const response = await get(`/admin/examination/question-paper/${existingPaperId}`);
                const paper = response.data;
                const paperSubjects = normalizeSubjectList(paper.subject);
                const fallbackSubjects = normalizeSubjectList(paper?.tuitionPeriod?.subjects);
                setFormData({
                    title: paper.title,
                    description: paper.description || "",
                    subject: joinSubjectChips(
                        paperSubjects.length ? paperSubjects : fallbackSubjects.length ? fallbackSubjects : normalizeSubjectList(periodSubjects)
                    ),
                    class: paper.class || studentClass || "",
                    examType: "mandatory",
                    examStartDateTime: paper.examStartDateTime
                        ? new Date(paper.examStartDateTime).toISOString().slice(0, 16)
                        : "",
                    durationInMinutes: paper.durationInMinutes,
                    fullMarks: paper.fullMarks,
                    passMarks: paper.passMarks,
                    questions: Array.isArray(paper.questions) ? paper.questions : [],
                });
                const seedQuestions = (paper.questions || []).filter(
                    (q: any) => q && typeof q === "object" && q._id
                );
                setAvailableQuestions(seedQuestions);
            } catch (error) {
                console.error("Error fetching question paper:", error);
                errorToast("Failed to fetch question paper");
                return;
            }
        } else {
            // New question paper - prefill with request data
            setFormData({
                title: "",
                description: "",
                subject: periodSubjects,
                class: studentClass || "",
                examType: "mandatory",
                examStartDateTime: "",
                durationInMinutes: 0,
                fullMarks: 0,
                passMarks: 0,
                questions: [],
            });
        }
        setFormModalOpen(true);
    };

    const handleCloseModal = () => {
        setFormModalOpen(false);
        setSelectedRequest(null);
        setRequestMeta({ examinationCreationRequestId: null, tuitionPeriodId: null });
        setAvailableQuestions([]);
        setErrors({});
        setFormData({
            title: "",
            description: "",
            subject: "",
            class: "",
            examType: "mandatory",
            examStartDateTime: "",
            durationInMinutes: 0,
            fullMarks: 0,
            passMarks: 0,
            questions: [],
        });
    };

    const validateForm = (): boolean => {
        const newErrors: typeof errors = {};

        if (!formData.title || formData.title.trim() === "") {
            newErrors.title = "Title is required";
        }

        if (!formData.subject || formData.subject.trim() === "") {
            newErrors.subject = "Subject is required";
        }

        if (!formData.class || String(formData.class).trim() === "") {
            newErrors.class = "Class is required";
        }

        if (!formData.examStartDateTime) {
            newErrors.examStartDateTime = "Exam start date and time is required";
        } else {
            const examDate = new Date(formData.examStartDateTime);
            const now = new Date();
            if (examDate < now) {
                newErrors.examStartDateTime = "Exam start date must be in the future";
            }
        }

        if (!formData.durationInMinutes || formData.durationInMinutes <= 0) {
            newErrors.durationInMinutes = "Duration must be greater than 0";
        }

        if (!formData.fullMarks || formData.fullMarks <= 0) {
            newErrors.fullMarks = "Full marks must be greater than 0";
        }

        if (!formData.passMarks || formData.passMarks <= 0) {
            newErrors.passMarks = "Pass marks must be greater than 0";
        } else if (formData.fullMarks && formData.passMarks > formData.fullMarks) {
            newErrors.passMarks = "Pass marks cannot be greater than full marks";
        }

        setErrors(newErrors);
        const messages = Object.values(newErrors).filter(Boolean);
        if (messages.length) {
            errorToast(messages.join(". "));
            return false;
        }
        return true;
    };

    const handleSubmit = async () => {
        if (!validateForm() || !selectedRequest) {
            return;
        }

        try {
            setLoading(true);
            const questionIds = (formData.questions || [])
                .map((q: any) => (typeof q === "string" ? q : q?._id))
                .filter(Boolean);
            const paperId = resolveId(selectedRequest.questionPaper);
            const payload = {
                ...formData,
                questions: questionIds,
                examinationCreationRequestId: requestMeta.examinationCreationRequestId ?? selectedRequest._id,
                tuitionPeriodId:
                    requestMeta.tuitionPeriodId ?? resolveId(selectedRequest.tuitionPeriod),
                adminEmail: signedAdmin.email,
                adminRole: signedAdmin.role,
                adminType: signedAdmin.type,
            };

            if (paperId) {
                await put(`/admin/examination/question-paper/${paperId}`, payload);
                successToast("Question paper updated successfully");
            } else {
                await post("/admin/examination/question-paper/create", payload);
                successToast("Question paper created successfully");
            }
            handleCloseModal();
            fetchRequests();
        } catch (error) {
            console.error("Error saving question paper:", error);
            errorToast("Failed to save question paper");
        } finally {
            setLoading(false);
        }
    };

    const handleToggleQuestion = (questionId: string) => {
        const currentQuestions = formData.questions || [];
        const questionIds = currentQuestions.map((q: any) => (typeof q === "string" ? q : q._id));
        const newQuestions = questionIds.includes(questionId)
            ? questionIds.filter((id) => id !== questionId)
            : [...questionIds, questionId];
        setFormData({ ...formData, questions: newQuestions });
        if (errors.questions && newQuestions.length > 0) {
            setErrors({ ...errors, questions: undefined });
        }
    };

    const columns: Column[] = [
        {
            select: "description",
            header: "Description",
            align: "left",
            render: (value: string) => (
                <Typography variant="body2" sx={{ maxWidth: 400, overflow: "hidden", textOverflow: "ellipsis" }}>
                    {value}
                </Typography>
            ),
        },
        {
            select: "tuitionPeriod",
            header: "Student",
            align: "left",
            render: (value: any) => {
                if (typeof value === "object" && value?.student) {
                    const student = value.student as any;
                    return (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            {student.profilePicture && (
                                <img
                                    src={student.profilePicture}
                                    alt={student.name}
                                    style={{ width: 30, height: 30, borderRadius: "50%" }}
                                />
                            )}
                            <Typography variant="body2">{student.name || "N/A"}</Typography>
                        </Box>
                    );
                }
                return "N/A";
            },
        },
        {
            select: "questionPaper",
            header: "Status",
            render: (value: any) => {
                if (value) {
                    return <Chip label="Question Paper Created" color="success" size="small" />;
                }
                return <Chip label="Pending" color="warning" size="small" />;
            },
        },
        {
            select: "createdAt",
            header: "Requested At",
            render: (value: string) =>
                value ? new Date(value).toLocaleString() : "N/A",
        },
        {
            select: "actions",
            header: "Actions",
            render: (_: any, row: ExaminationCreationRequestInterface) => (
                <Button
                    size="small"
                    variant="outlined"
                    startIcon={<Visibility />}
                    onClick={() => handleOpenModal(row)}
                    sx={{
                        borderColor: "#fcd307",
                        color: "#fcd307",
                        "&:hover": {
                            borderColor: "#e6be06",
                            backgroundColor: "rgba(252, 211, 7, 0.1)",
                        },
                    }}
                >
                    {row.questionPaper ? "View/Edit Paper" : "Create Paper"}
                </Button>
            ),
        },
    ];

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
                <Typography variant="h4" className="text-white">Mandatory Exam Requests</Typography>
            </Box>

            <TextField
                fullWidth
                label="Search Requests"
                variant="outlined"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                sx={{
                    mb: 2,
                    "& .MuiOutlinedInput-root": {
                        color: "#ffffff",
                        "& fieldset": {
                            borderColor: "#ffffff",
                        },
                        "&:hover fieldset": {
                            borderColor: "#ffffff",
                        },
                        "&.Mui-focused fieldset": {
                            borderColor: "#ffffff",
                        },
                    },
                    "& .MuiInputLabel-root": {
                        color: "#ffffff",
                        "&.Mui-focused": {
                            color: "#ffffff",
                        },
                    },
                    "& .MuiInputBase-input": {
                        color: "#ffffff",
                    },
                }}
            />

            <TableComponent
                data={filteredRequests}
                columns={columns}
            />

            <QuestionPaperFormDialog
                open={formModalOpen}
                mode={selectedRequest?.questionPaper ? "edit" : "create"}
                onClose={handleCloseModal}
                onSubmit={handleSubmit}
                loading={loading}
                questionPaperId={resolveId(selectedRequest?.questionPaper)}
                signedAdmin={signedAdmin}
                onPaperUpdated={(paper) => {
                    if (paper?._id) {
                        setSelectedRequest((prev) =>
                            prev ? { ...prev, questionPaper: paper._id as any } : prev
                        );
                    }
                    fetchRequests();
                }}
                subjects={subjects}
                availableQuestions={availableQuestions}
                formData={formData}
                setFormData={setFormData}
                errors={errors as any}
                setErrors={setErrors as any}
                onToggleQuestion={handleToggleQuestion}
                onGoToQuestionsPage={() => navigate("/examination/questions")}
                titleText={selectedRequest?.questionPaper ? "Edit Question Paper" : "Create Question Paper"}
                submitText={selectedRequest?.questionPaper ? "Update" : "Create"}
                createExtras={{
                    examinationCreationRequestId: requestMeta.examinationCreationRequestId,
                    tuitionPeriodId: requestMeta.tuitionPeriodId,
                }}
            />
        </Box>
    );
};

export default Requests;
